import { useState, type FormEvent } from "react";
import { Button, Input, FormField, Heading, Text } from "@/components/ds";
import styles from "./OnboardingStep.module.css";

interface OnboardingInviteStepProps {
  readonly onComplete: () => void;
  readonly onSkip: () => void;
}

export function OnboardingInviteStep({ onComplete, onSkip }: OnboardingInviteStepProps) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [nameError, setNameError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();

    const trimmedName = name.trim();
    const trimmedEmail = email.trim().toLowerCase();
    let hasError = false;

    if (!trimmedName) {
      setNameError("Name is required");
      hasError = true;
    }

    if (!trimmedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setEmailError("Please enter a valid email address");
      hasError = true;
    }

    if (hasError) return;

    setLoading(true);
    try {
      const response = await fetch("/api/team/invites", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: trimmedName,
          email: trimmedEmail,
          isOwner: false,
          isCleaner: true,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        if (data.error?.fields) {
          if (data.error.fields.name) setNameError(data.error.fields.name);
          if (data.error.fields.email) setEmailError(data.error.fields.email);
        } else {
          setEmailError(data.error?.message ?? "Something went wrong. Please try again.");
        }
        return;
      }

      onComplete();
    } finally {
      setLoading(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className={styles.form}>
      <div className={styles.header}>
        <Heading level={1} size="h3">Invite your first cleaner</Heading>
        <Text size="sm" color="muted">
          Nearly there! They'll get an email with a link to join your team.
        </Text>
      </div>

      <FormField label="Their name" htmlFor="onboarding-cleaner-name" error={nameError}>
        <Input
          name="cleanerName"
          id="onboarding-cleaner-name"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setNameError("");
          }}
          placeholder="e.g. Sarah Jones"
          error={!!nameError}
        />
      </FormField>

      <FormField label="Their email" htmlFor="onboarding-cleaner-email" error={emailError}>
        <Input
          name="cleanerEmail"
          id="onboarding-cleaner-email"
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setEmailError("");
          }}
          placeholder="e.g. sarah@example.com"
          error={!!emailError}
        />
      </FormField>

      <div className={styles.actions}>
        <Button type="submit" fullWidth disabled={loading}>
          {loading ? "Sending..." : "Send invite"}
        </Button>
        <button type="button" className={styles.skipButton} onClick={onSkip} disabled={loading}>
          I'll do this later
        </button>
      </div>
    </form>
  );
}
